import { useState } from "react";
import { motion } from "framer-motion";
import { Flame, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import BetControl from "../components/BetControl";
import Confetti from "../components/Confetti";
import CoinRain from "../components/CoinRain";
import { PlayingCard } from "../components/PlayingCard";
import { ShareWin } from "../components/WinShareCard";
import { formatCoins } from "../lib/format";
import { playSpin, playWin, playLose } from "../lib/sound";

const SIDES = [
  { id: "dragon", key: "dragon", mult: "1.95×", color: "from-rose-500/30 to-amber-500/10", ring: "border-rose-400" },
  { id: "tie", key: "tie", mult: "8×", color: "from-amber-400/30 to-amber-600/10", ring: "border-amber-400" },
  { id: "tiger", key: "tiger", mult: "1.95×", color: "from-orange-500/30 to-yellow-500/10", ring: "border-orange-400" },
];

export default function DragonTiger() {
  const { t } = useLang();
  const { user, setCoins } = useAuth();
  const [bet, setBet] = useState(100);
  const [side, setSide] = useState("dragon");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [confetti, setConfetti] = useState(0);
  const [rain, setRain] = useState(0);

  const deal = async () => {
    if (busy) return;
    setBusy(true);
    setResult(null);
    playSpin();
    try {
      const { data } = await api.post("/games/dragontiger", { bet, side });
      setResult(data);
      setCoins(data.coins);
      setTimeout(() => {
        if (data.win > 0) { setConfetti(Date.now()); setRain(Date.now()); playWin(data.win); toast.success(`${t("youWon")} +${formatCoins(data.win)}!`); }
        else { playLose(); toast(`${t("youLost")} (-${formatCoins(bet)})`); }
      }, 900);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTimeout(() => setBusy(false), 500);
    }
  };

  const Slot = ({ label, card, id }) => {
    const isWin = result?.winner === id;
    return (
      <div className={`flex-1 rounded-xl border p-4 min-h-[150px] flex flex-col items-center justify-center gap-2 transition-colors ${isWin ? "border-emerald-500/60 bg-emerald-500/10" : side === id ? "border-amber-400/60 bg-amber-500/5" : "border-white/10 bg-black/30"}`} data-testid={`dt-${id}-slot`}>
        <p className={`text-xs font-bold uppercase tracking-wider ${side === id ? "text-amber-300" : "text-slate-400"}`}>{label}</p>
        {result ? <PlayingCard card={card} highlight={isWin} testid={`dt-${id}-card`} /> :
          <div className="h-24 w-16 rounded-xl border-2 border-dashed border-amber-500/30 grid place-items-center text-amber-500/40"><Flame size={22} /></div>}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Confetti fire={confetti} />
      <CoinRain fire={rain} />
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold gold-text">{t("dragonTiger")}</h1>
        <p className="text-sm text-slate-400 mt-1">{t("dragonTigerDesc")}</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 mw-card p-6 space-y-4" data-testid="dt-table">
          <div className="flex gap-3 items-stretch">
            <Slot label={t("dragon")} card={result?.dragon} id="dragon" />
            <div className="grid place-items-center">
              <span className="font-display text-2xl font-extrabold text-amber-400">VS</span>
            </div>
            <Slot label={t("tiger")} card={result?.tiger} id="tiger" />
          </div>
          {result && (
            <motion.p initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="text-center font-display text-2xl font-extrabold" style={{ color: result.won ? "#00E676" : "#FF3366" }} data-testid="dt-result">
              {result.winner === "tie" ? "TIE · " : `${t(result.winner)} · `}
              {result.won ? `${t("youWon")} +${formatCoins(result.win)}` : `${t("youLost")} -${formatCoins(bet)}`}
            </motion.p>
          )}
          {result?.won && <div className="flex justify-center"><ShareWin amount={result.win} game="dragontiger" /></div>}
        </div>

        <div className="mw-card p-6 space-y-5">
          <div className="grid grid-cols-3 gap-2">
            {SIDES.map((s) => (
              <button key={s.id} onClick={() => setSide(s.id)} disabled={busy} data-testid={`dt-side-${s.id}`}
                className={`rounded-xl py-3 font-bold border transition-colors bg-gradient-to-br ${s.color} ${side === s.id ? `${s.ring} ring-2 ring-amber-400/40` : "border-white/10"}`}>
                <span className="block">{t(s.key)}</span>
                <span className="block text-[11px] font-mono-num text-amber-300">{s.mult}</span>
              </button>
            ))}
          </div>
          <BetControl bet={bet} setBet={setBet} max={user?.coins} disabled={busy} />
          <button onClick={deal} disabled={busy} data-testid="dt-deal-btn" className="w-full btn-gold rounded-xl py-3 text-lg flex items-center justify-center gap-2">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <Flame size={18} />} {t("deal")}
          </button>
        </div>
      </div>
    </div>
  );
}
